import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { postService } from '../api/services';
import './CommandPalette.css';

// 全域指令面板：以 Cmd/Ctrl+K 開啟，提供鍵盤快速導覽與即時文章搜尋。
// 設計為全站只掛載一次（置於 AppContent 內，才能取得 Router/Auth/Theme context）。
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]); // 即時搜尋到的文章
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // 註冊全域開關：Cmd/Ctrl+K 切換，另提供自訂事件供導覽列按鈕觸發
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('tech-forum:open-command-palette', onOpenEvent);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('tech-forum:open-command-palette', onOpenEvent);
    };
  }, []);

  // 開啟時聚焦輸入框並鎖定背景捲動；關閉時重置查詢狀態
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setResults([]);
    setActive(0);
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 30);
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = '';
    };
  }, [open]);

  // 每次輸入變動時把選取項目歸零，避免停留在已被過濾掉的索引上
  useEffect(() => { setActive(0); }, [query]);

  // 即時文章搜尋（防抖 250ms）；少於 2 字不打 API，避免噪音與多餘請求
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(() => {
      postService.list({ search: q, limit: 6, sort: 'new' })
        .then((res) => setResults(res.data || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  const close = () => setOpen(false);
  // 執行導覽：先關閉面板再跳轉，避免面板殘留
  const go = (path) => { setOpen(false); navigate(path); };

  // 靜態指令清單，依登入狀態與權限動態組成（每次 render 重建，內容輕量）
  const actions = [
    { id: 'home', icon: '🏠', label: '前往首頁', kw: 'home 首頁 shouye', run: () => go('/') },
    { id: 'rankings', icon: '🏆', label: '排行榜', kw: 'rankings 排行榜 paihang', run: () => go('/rankings') },
  ];
  if (isAuthenticated) {
    actions.push(
      { id: 'create', icon: '✏️', label: '發表新文章', kw: 'create new post 發文 撰寫 新增', run: () => go('/posts/create') },
      { id: 'users', icon: '👥', label: '人員名錄', kw: 'users members 名錄 成員', run: () => go('/users') },
      { id: 'bookmarks', icon: '★', label: '我的收藏', kw: 'bookmarks 收藏 favorite', run: () => go('/bookmarks') },
      { id: 'subs', icon: '🔖', label: '我的訂閱', kw: 'subscriptions 訂閱 follow', run: () => go('/subscriptions') },
      { id: 'profile', icon: '👤', label: '我的資料', kw: 'profile 個人 資料 account', run: () => go('/profile') },
      { id: 'theme', icon: theme === 'light' ? '🌙' : '☀️', label: theme === 'light' ? '切換深色模式' : '切換淺色模式', kw: 'theme dark light 深色 淺色 主題', run: () => { setOpen(false); toggleTheme(); } },
      { id: 'logout', icon: '🚪', label: '登出', kw: 'logout signout 登出', run: () => { setOpen(false); logout(); navigate('/'); } },
    );
    if (user?.role === 'ADMIN') {
      actions.push({ id: 'admin', icon: '⚙️', label: '管理後台', kw: 'admin 管理 後台', run: () => go('/admin') });
    }
  } else {
    actions.push(
      { id: 'login', icon: '🔑', label: '登入', kw: 'login 登入', run: () => go('/login') },
      { id: 'register', icon: '📝', label: '註冊', kw: 'register 註冊', run: () => go('/register') },
    );
  }

  // 依輸入字串過濾靜態指令（比對顯示文字與關鍵字）
  const q = query.trim().toLowerCase();
  const filteredActions = q ? actions.filter((a) => a.label.toLowerCase().includes(q) || a.kw.toLowerCase().includes(q)) : actions;

  // 組合最終清單：靜態指令 →「搜尋全部文章」→ 即時文章結果
  const items = filteredActions.map((a) => ({ ...a, type: 'action' }));
  if (query.trim()) {
    items.push({ id: 'search-all', type: 'search', icon: '🔍', label: `搜尋全部文章：「${query.trim()}」`, run: () => go(`/?search=${encodeURIComponent(query.trim())}`) });
  }
  results.forEach((p) => {
    items.push({ id: `post-${p.id}`, type: 'post', icon: '📄', label: p.title, sub: p.author_name, run: () => go(`/posts/${p.id}`) });
  });

  const activeIdx = items.length ? Math.min(active, items.length - 1) : 0;

  // 鍵盤操作：上下移動選取、Enter 執行、Esc 關閉
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (items.length ? (i + 1) % items.length : 0)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (items.length ? (i - 1 + items.length) % items.length : 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[activeIdx];
      if (it) it.run();
    }
  };

  if (!open) return null;

  return (
    <div className="cmdk-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="指令面板">
        <div className="cmdk-input-row">
          <span className="cmdk-prompt">⌘</span>
          <input
            ref={inputRef}
            className="cmdk-input"
            placeholder="輸入指令或搜尋文章..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="cmdk-kbd">Esc</kbd>
        </div>

        <div className="cmdk-list">
          {items.length === 0 ? (
            <div className="cmdk-empty">沒有符合的結果</div>
          ) : (
            items.map((it, idx) => (
              <button
                key={it.id}
                type="button"
                className={`cmdk-item ${idx === activeIdx ? 'active' : ''}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => it.run()}
              >
                <span className="cmdk-item-icon">{it.icon}</span>
                <span className="cmdk-item-label">{it.label}</span>
                {it.sub && <span className="cmdk-item-sub">{it.sub}</span>}
                {it.type === 'post' && <span className="cmdk-item-tag">文章</span>}
              </button>
            ))
          )}
        </div>

        <div className="cmdk-footer">
          <span><kbd className="cmdk-kbd">↑</kbd><kbd className="cmdk-kbd">↓</kbd> 選擇</span>
          <span><kbd className="cmdk-kbd">↵</kbd> 開啟</span>
          <span><kbd className="cmdk-kbd">Esc</kbd> 關閉</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
